/*
 * Turning errors into words. A problem's `code` maps to `errors:codes.<code>` ({ title, detail }); unknown
 * codes fall back by HTTP status, then to a generic message. Field codes map to `errors:fields.<code>`.
 */
import { isApiError, type ApiError } from '@/api/client'
import { i18n } from '@/i18n'
import { DEFAULT_LOCALE, isLocale } from '@/i18n/locale'
import { formatDate } from './format'

// Keys here are built at runtime, so this uses an untyped view of i18n.t. Components re-render on a
// language change (useTranslation), so the current language is always used.
const tt = i18n.t.bind(i18n) as unknown as (k: string, o?: Record<string, unknown>) => string

/** Title and detail for any error (ApiError problem code, then HTTP status, then generic). */
export function problemMessage(error: unknown): { title: string; detail: string } {
  const exists = (k: string) => tt(k, { defaultValue: '' }) !== ''
  if (isApiError(error)) {
    const base = `errors:codes.${error.code}`
    if (exists(`${base}.title`)) {
      const vars = problemVars(error)
      return { title: tt(`${base}.title`, vars), detail: tt(`${base}.detail`, vars) }
    }
    const byStatus = `errors:status.${error.status}`
    if (exists(`${byStatus}.title`))
      return { title: tt(`${byStatus}.title`), detail: tt(`${byStatus}.detail`) }
  }
  return { title: tt('errors:generic.title'), detail: tt('errors:generic.detail') }
}

/** Values a problem's copy may use: `{{date}}` is `availableAt` (409 transfer_too_soon), in the current language. */
function problemVars(error: ApiError): Record<string, unknown> {
  const locale = isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE
  const at = error.problem.availableAt
  return { date: typeof at === 'string' ? formatDate(at, locale, 'long') : '' }
}

/** Translates a field error code (from zod or the API) into a message; passes unknown text through. */
export function fieldMessage(code: string | undefined): string | undefined {
  if (!code) return undefined
  const msg = tt(`errors:fields.${code}`, { defaultValue: '' })
  return msg || code
}
